// [전체 코드 미사용/주석 삭제 금지]

// Base imports
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from 'config/apiConfig';

// CSS imports
import 'styles/Font.css';

// Image imports
import searchIcon from 'assets/images/icon/search-icon.svg';
import noImage from 'assets/images/logo/BranchNoImage.png';

// Components imports
import TopNavigator from 'components/layout/TopNavigator/TopNavigator';
import usePreventInputScroll from 'hooks/usePreventInputScroll';
import usePreventHorizontalScroll from 'hooks/usePreventHorizontalScroll';
import ConfirmModal from 'components/ui/Modals/ConfirmModal/ConfirmModal';

// 지점 목록 API 호출 (가입 전 단계이므로 인증 헤더 불필요 - /api/store는 공개 엔드포인트)
const fetchBranchListApi = async () => {
    const API_URL = `${API_BASE_URL}/store`;

    const response = await fetch(API_URL, {
        method: 'GET',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    if (!response.ok) {
        throw new Error(`API 호출 실패: HTTP 상태 ${response.status}`);
    }

    const data = await response.json();
    return data?.data?.list || [];
};

// 회원가입 - 지점 선택 페이지
function BranchSelect() {
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const navigate = useNavigate();

    // 동적 높이 측정을 위한 ref 및 state
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // 상태
    const [searchText, setSearchText] = useState('');
    const [branchList, setBranchList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);

    // 알림 모달 상태
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });

    // 헤더 높이 측정 로직
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        measureHeight();
        setTimeout(measureHeight, 100);

        window.addEventListener('resize', measureHeight);
        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // 지점 목록 로딩
    const fetchBranchList = async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await fetchBranchListApi();
            setBranchList(Array.isArray(list) ? list : []);
        } catch (e) {
            setError(e.message || '지점 목록을 불러오는 데 실패했습니다.');
            setBranchList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranchList();
    }, []);

    const closeAlertModal = () => setAlertModal(prev => ({ ...prev, isOpen: false }));

    const handleSelectBranch = (branch) => {
        setSelectedBranch(branch);
    };

    const handleNextClick = () => {
        if (!selectedBranch) return;

        const dataFromStorage = sessionStorage.getItem('tempSignUpData');
        const existingData = dataFromStorage ? JSON.parse(dataFromStorage) : {};
        const updatedData = {
            ...existingData,
            brKey: selectedBranch.brKey,
            brName: selectedBranch.brName,
        };
        sessionStorage.setItem('tempSignUpData', JSON.stringify(updatedData));

        navigate('/signup');
    };

    const filteredList = (Array.isArray(branchList) ? branchList : []).filter(branch => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        return (
            branch.brName?.toLowerCase().includes(searchLower) ||
            branch.brAddr?.toLowerCase().includes(searchLower)
        );
    });

    const dynamicMainStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    const renderList = () => {
        if (loading) {
            return <p style={staticStyles.infoMessage}>지점 목록을 불러오는 중...</p>;
        }

        if (error) {
            return (
                <div style={staticStyles.errorContainer}>
                    <p style={staticStyles.errorMessage}>{error}</p>
                    <button onClick={fetchBranchList} style={staticStyles.retryButton}>다시 시도</button>
                </div>
            );
        }

        if (filteredList.length === 0) {
            return <p style={staticStyles.infoMessage}>검색 결과가 없습니다.</p>;
        }

        return (
            <div style={staticStyles.listWrapper}>
                {filteredList.map(branch => {
                    const isSelected = selectedBranch?.brKey === branch.brKey;
                    return (
                        <div
                            key={branch.brKey}
                            style={{
                                ...staticStyles.branchListItem,
                                ...(isSelected ? staticStyles.branchListItemSelected : {}),
                            }}
                            onClick={() => handleSelectBranch(branch)}
                        >
                            <div style={staticStyles.branchImgDiv}>
                                <img
                                    src={branch.brImg || noImage}
                                    alt={branch.brName}
                                    style={staticStyles.branchImage}
                                />
                            </div>
                            <div style={staticStyles.branchInfoDiv}>
                                <p style={staticStyles.branchName}>{branch.brName}</p>
                                <span style={staticStyles.branchAddress}>{branch.brAddr}</span>
                            </div>
                            <div style={{
                                ...staticStyles.radioCircle,
                                ...(isSelected ? staticStyles.radioCircleSelected : {}),
                            }} />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div style={staticStyles.backgroundWrapper}>
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div style={staticStyles.statusBar} />
                <TopNavigator title="지점 선택" />
            </div>

            <main style={dynamicMainStyle}>
                <div style={staticStyles.infoField}>
                    <p style={staticStyles.infoTitle}>이용하실 지점을 선택해주세요</p>
                    <p style={staticStyles.infoSubtitle}>선택한 지점은 마이페이지에서 확인할 수 있어요</p>
                </div>

                <div style={staticStyles.searchContainer}>
                    <div style={staticStyles.searchInputWrapper}>
                        <img src={searchIcon} alt="검색" style={staticStyles.searchIcon} />
                        <input
                            type="text"
                            placeholder="지역, 지하철역, 매장명"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={staticStyles.searchInput}
                        />
                    </div>
                </div>

                {renderList()}
            </main>

            <div style={staticStyles.bottomButtonWrapper}>
                <button
                    style={{
                        ...staticStyles.nextButton,
                        ...(selectedBranch ? staticStyles.nextButtonActive : {}),
                    }}
                    disabled={!selectedBranch}
                    onClick={handleNextClick}
                >
                    <p style={staticStyles.nextButtonLabel}>다음</p>
                </button>
            </div>

            <ConfirmModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                onConfirm={closeAlertModal}
                title={alertModal.title}
                message={alertModal.message}
                showCancel={false}
            />
        </div>
    );
}

/** @type {React.CSSProperties} */
const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh',
        display: 'flex', flexDirection: 'column', margin: '0 auto',
        position: 'relative', overflow: 'hidden',
    },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: '430px', zIndex: 100, backgroundColor: '#0D042D',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    mainContent: {
        flex: 1, overflowY: 'auto', width: '100%', maxWidth: '430px', margin: '0 auto',
        paddingTop: '0',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 24px) + 94px)',
        paddingLeft: '24px', paddingRight: '24px', boxSizing: 'border-box',
    },
    infoField: {
        width: '100%', display: 'flex', flexDirection: 'column',
        alignItems: 'flex-start', marginBottom: '24px', marginTop: '25px',
    },
    infoTitle: {
        fontFamily: '"Pretendard", sans-serif', fontWeight: '700',
        fontSize: 'clamp(24px, 6.4vw, 28px)', color: '#F5F8F9',
        lineHeight: 'clamp(34px, 9.07vw, 39px)', margin: '0 0 clamp(12px, 3.2vw, 14px) 0',
    },
    infoSubtitle: {
        fontFamily: '"Pretendard", sans-serif', fontWeight: '500',
        fontSize: 'clamp(16px, 4.27vw, 18px)', color: '#9A9A9A',
        lineHeight: 'clamp(24px, 6.4vw, 28px)', margin: 0,
    },
    searchContainer: { width: '100%', boxSizing: 'border-box', marginBottom: '12px' },
    searchInputWrapper: {
        width: '100%', height: '48px', background: '#1D1A3E', borderRadius: '8px',
        display: 'flex', alignItems: 'center', padding: '0 16px', boxSizing: 'border-box',
    },
    searchIcon: { width: '24px', height: '24px', marginRight: '8px', flexShrink: 0 },
    searchInput: {
        flex: 1, background: 'transparent', border: 'none', outline: 'none',
        fontFamily: 'Pretendard', fontWeight: '500', fontSize: '16px', color: '#FCFCFA',
    },
    listWrapper: { width: '100%', display: 'flex', flexDirection: 'column' },
    branchListItem: {
        width: '100%', display: 'flex', alignItems: 'center',
        padding: '16px 12px', borderBottom: '1px solid #1D1A3E',
        borderRadius: '8px', boxSizing: 'border-box', cursor: 'pointer',
        border: '1px solid transparent',
    },
    branchListItemSelected: {
        background: 'linear-gradient(#1B1931, #1B1931), linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
    },
    branchImgDiv: {
        width: '64px', height: '64px', borderRadius: '8px', overflow: 'hidden',
        marginRight: '16px', flexShrink: 0, backgroundColor: '#333',
    },
    branchImage: { width: '100%', height: '100%', objectFit: 'cover' },
    branchInfoDiv: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
    branchName: {
        fontFamily: 'Pretendard', fontWeight: '600', fontSize: '16px',
        color: '#FCFCFA', margin: '0 0 4px 0',
    },
    branchAddress: {
        fontFamily: 'Pretendard', fontWeight: '400', fontSize: '12px',
        color: '#A0A0A0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    radioCircle: {
        width: '20px', height: '20px', borderRadius: '50%',
        border: '2px solid #4D4D6B', flexShrink: 0, marginLeft: '12px',
        boxSizing: 'border-box',
    },
    radioCircleSelected: {
        border: '6px solid #8052FF',
    },
    infoMessage: {
        width: '100%', textAlign: 'center', color: '#9A9A9A',
        fontFamily: 'Pretendard', fontSize: '14px', padding: '40px 0',
    },
    errorContainer: { width: '100%', textAlign: 'center', padding: '40px 0' },
    errorMessage: { color: '#FF4D4D', fontFamily: 'Pretendard', fontSize: '14px', marginBottom: '12px' },
    retryButton: {
        background: '#1D1A3E', color: '#FCFCFA', border: 'none', borderRadius: '8px',
        padding: '8px 16px', cursor: 'pointer', fontFamily: 'Pretendard',
    },
    bottomButtonWrapper: {
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: '430px',
        padding: '16px 24px calc(env(safe-area-inset-bottom, 24px) + 16px)',
        boxSizing: 'border-box', zIndex: 100, backgroundColor: '#0D042D',
    },
    nextButton: {
        width: '100%', height: 'clamp(56px, 14.93vw, 64px)', border: 'none',
        borderRadius: 'clamp(6px, 1.6vw, 7px)', background: '#3A3A4A',
        cursor: 'not-allowed', margin: 0, display: 'flex',
        justifyContent: 'center', alignItems: 'center',
    },
    nextButtonActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        cursor: 'pointer',
    },
    nextButtonLabel: {
        fontFamily: '"Pretendard", sans-serif', fontWeight: '700',
        fontSize: 'clamp(16px, 4.27vw, 18px)', color: '#FFFFFF', margin: 0,
    },
};

export default BranchSelect;
